/**
 * GIWAHS — Memberships Client
 * ---------------------------------------------------------------------
 * Thin REST wrapper over the FastAPI router at /api/memberships/*.
 *
 * The backend router is feature-flag-gated by MEMBERSHIPS_ENABLED. When
 * the flag is OFF, every call returns HTTP 404. This client detects that
 * state and returns `{ disabled: true }` so pages can render a tidy
 * "coming soon" placeholder instead of a generic error.
 *
 * The frontend has its own kill switch via REACT_APP_MEMBERSHIPS_UI.
 * When unset/false, isMembershipsUiEnabled() returns false and the
 * client refuses to make any network call. Routes can early-return
 * before mounting the page.
 *
 * No business logic lives here — this is transport only.
 */
import { getSupabaseClient } from './supabaseClient';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export const isMembershipsUiEnabled = () =>
  String(process.env.REACT_APP_MEMBERSHIPS_UI || '').toLowerCase() === 'true';

async function _bearer() {
  // We hand the Supabase JWT to the FastAPI backend so it can resolve
  // the caller against Supabase Auth. If there's no session the call
  // is anonymous (used by the public allocations endpoint).
  try {
    const sb = getSupabaseClient();
    if (!sb) return null;
    const { data } = await sb.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}

async function _call(path, { method = 'GET', body, auth = true } = {}) {
  if (!isMembershipsUiEnabled()) return { disabled: true, reason: 'ui_flag_off' };
  if (!API_BASE) return { disabled: true, reason: 'api_base_missing' };

  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const tk = await _bearer();
    if (tk) headers['Authorization'] = `Bearer ${tk}`;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 404) {
    return { disabled: true, reason: 'router_disabled' };
  }
  if (res.status === 401) return { ok: false, code: 'unauthenticated' };
  if (res.status === 403) {
    let detail = null; try { detail = (await res.json()).detail; } catch {}
    return { ok: false, code: 'forbidden', detail };
  }

  let payload = null;
  try { payload = await res.json(); } catch {}
  if (!res.ok) {
    return { ok: false, code: 'http_error', status: res.status, detail: payload?.detail };
  }
  return { ok: true, ...payload };
}

export const membershipsClient = {
  // Public — anonymous-readable allocation gauge.
  async listAllocations() {
    return _call('/api/memberships/allocations', { auth: false });
  },
  // Caller's organisation + membership snapshot.
  async me() {
    return _call('/api/memberships/me');
  },
  // Begin Stripe Checkout (subscription mode). Returns { ok, session_url } or { ok:false, reason:"waitlist_required" }.
  async createCheckoutSession({ tier, organisation_id, success_url, cancel_url }) {
    return _call('/api/memberships/checkout/session', {
      method: 'POST',
      body: { tier, organisation_id, success_url, cancel_url },
    });
  },
  // Returns a Stripe Customer Portal URL to manage the membership.
  async createPortalSession({ return_url } = {}) {
    return _call('/api/memberships/portal/session', { method: 'POST', body: { return_url } });
  },
  async upgrade({ target_tier }) {
    return _call('/api/memberships/upgrade', { method: 'POST', body: { target_tier } });
  },
  async scheduleDowngrade({ target_tier, confirm }) {
    return _call('/api/memberships/downgrade-schedule', { method: 'POST', body: { target_tier, confirm } });
  },
  async cancelDowngrade() {
    return _call('/api/memberships/downgrade-cancel', { method: 'POST' });
  },
  async joinWaitlist({ tier, organisation_id }) {
    return _call('/api/memberships/waitlist/join', { method: 'POST', body: { tier, organisation_id } });
  },
  async markOnboardingComplete({ membership_id }) {
    return _call('/api/memberships/onboarding/complete', { method: 'POST', body: { membership_id } });
  },
  async markVerificationSubmitted({ membership_id }) {
    return _call('/api/memberships/verification/submitted', { method: 'POST', body: { membership_id } });
  },

  // Admin — only succeed for users with is_admin()=true (server-enforced).
  admin: {
    async overview() { return _call('/api/memberships/admin/overview'); },
    async waitlist({ tier } = {}) {
      const qs = tier ? `?tier=${encodeURIComponent(tier)}` : '';
      return _call(`/api/memberships/admin/waitlist${qs}`);
    },
    async promoteFromWaitlist({ waitlist_id }) {
      return _call('/api/memberships/admin/waitlist/promote', { method: 'POST', body: { waitlist_id } });
    },
    async rebalanceAllocations({ wave, tier, delta }) {
      return _call('/api/memberships/admin/allocations/rebalance', {
        method: 'POST', body: { wave, tier, delta },
      });
    },
  },
};

// Static tier metadata — used for cards / pricing display. Keep
// canonical values in sync with the backend `membership_products.json`
// once Step 4 setup script is run live.
export const TIER_META = {
  gold_founder:        { label: 'Gold Founding Partner',       family: 'gold',       founder: true,  founder_price_usd: 490,  standard_price_usd: 700,  capacity: 250 },
  elite_founder:       { label: 'Elite Founding Partner',      family: 'elite',      founder: true,  founder_price_usd: 1490, standard_price_usd: 2100, capacity: 200 },
  enterprise_founder:  { label: 'Enterprise Founding Partner', family: 'enterprise', founder: true,  founder_price_usd: 4990, standard_price_usd: 7100, capacity: 50  },
  gold_standard:       { label: 'Gold Member',                 family: 'gold',       founder: false, founder_price_usd: null, standard_price_usd: 700  },
  elite_standard:      { label: 'Elite Member',                family: 'elite',      founder: false, founder_price_usd: null, standard_price_usd: 2100 },
  enterprise_standard: { label: 'Enterprise Member',           family: 'enterprise', founder: false, founder_price_usd: null, standard_price_usd: 7100 },
};

export const FOUNDER_TIERS  = ['gold_founder', 'elite_founder', 'enterprise_founder'];
export const STANDARD_TIERS = ['gold_standard', 'elite_standard', 'enterprise_standard'];

export const fmtUSD = (n) => (n == null ? '—' : `US$${Number(n).toLocaleString('en-US')}`);

export default membershipsClient;
